import { useId } from "react";
import { motion } from "framer-motion";
import { CAJA, DENTRO, FUERA, ROJO, capas } from "@/lib/logoAlpha.mjs";

/**
 * El logo de Academia Alpha. **El único sitio del frontend donde se dibuja la marca.**
 *
 * HABIA TRES LOGOS DISTINTOS y nadie lo había notado: los PDF llevaban en la portada uno de
 * dos picos que se leía como una M, esta pantalla dibujaba un pico suelto con una raya
 * debajo, y el que se enseñaba fuera era un tercero. Cada uno estaba dibujado a mano en su
 * fichero, así que ninguno sabía de los otros dos y cambiar uno no cambiaba nada.
 *
 * Ahora los trazos viven en `lib/logoAlpha.mjs` y de ahí salen los tres sitios: esto, el PNG
 * de la portada de los PDF y el favicon. Lo vigila `tools/check_logo.py`.
 *
 * `useId` Y NO `Math.random()`. Los filtros SVG se referencian por id, y con dos logos en la
 * misma página —la barra y el pie— dos ids iguales hacen que el segundo herede el filtro del
 * primero. Con `Math.random()` además el id cambia entre el HTML del servidor y el del
 * navegador, que es la receta de un parpadeo que nadie sabe de dónde sale.
 */
export default function AALogo({
  size = 40,
  variant = "dark",       // "dark" = marca oscura sobre fondo claro; "light" = al revés
  lockup = "icono",       // "icono" | "vertical" | "horizontal"
  className = "",
}) {
  const idp = useId().replace(/:/g, "");
  const oscuro = variant === "light";   // ¿la marca va sobre fondo oscuro?
  const pequeno = size <= 40;
  const c = capas({ oscuro, pequeno });
  const alto = Math.round((size * CAJA.alto) / CAJA.ancho);

  const marca = (
    <motion.svg
      width={size}
      height={alto}
      viewBox={`0 0 ${CAJA.ancho} ${CAJA.alto}`}
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      initial={{ opacity: 0, y: -4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, ease: [0.2, 0.8, 0.2, 1] }}
      role="img"
      aria-label="Academia Alpha"
    >
      <defs>
        <filter id={`${idp}-humo`} x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur stdDeviation={c.humo.desenfoque[0]} result="a" />
          <feGaussianBlur stdDeviation={c.humo.desenfoque[1]} in="SourceGraphic" result="b" />
          <feMerge>
            <feMergeNode in="a" /><feMergeNode in="a" /><feMergeNode in="b" />
          </feMerge>
        </filter>
        <filter id={`${idp}-luz`} x="-60%" y="-60%" width="220%" height="220%">
          <feGaussianBlur stdDeviation={c.luz.desenfoque} result="d" />
          <feMerge><feMergeNode in="d" /><feMergeNode in="SourceGraphic" /></feMerge>
        </filter>
      </defs>

      {/* El humo: lo que hace que parezca fuego y no una línea. */}
      <g filter={`url(#${idp}-humo)`} opacity={c.humo.opacidad}>
        <path d={FUERA} stroke={ROJO} strokeWidth={c.humo.fuera} strokeLinejoin="miter" fill="none" />
        <path d={DENTRO} stroke={ROJO} strokeWidth={c.humo.dentro} strokeLinejoin="miter" fill="none" />
      </g>
      {/* La brasa. */}
      <path d={FUERA} stroke={ROJO} strokeWidth={c.brasa.fuera} strokeLinejoin="miter" fill="none" opacity={c.brasa.opacidad} />
      <path d={DENTRO} stroke={ROJO} strokeWidth={c.brasa.dentro} strokeLinejoin="miter" fill="none" opacity={c.brasa.opacidad} />
      {/* La luz. */}
      <g filter={`url(#${idp}-luz)`}>
        <path d={FUERA} stroke={c.core} strokeWidth={c.luz.fuera} strokeLinejoin="miter" fill="none" />
        <path d={DENTRO} stroke={c.core} strokeWidth={c.luz.dentro} strokeLinejoin="miter" fill="none" />
      </g>
    </motion.svg>
  );

  if (lockup === "icono") {
    return <div className={`inline-flex ${className}`} data-testid="aa-logo">{marca}</div>;
  }

  const palabra = <Palabra color={c.core} centrado={lockup === "vertical"} escala={size / 44} />;

  return (
    <div
      className={`inline-flex ${lockup === "vertical" ? "flex-col items-center gap-3" : "items-center gap-3"} ${className}`}
      data-testid="aa-logo"
    >
      {marca}
      {palabra}
    </div>
  );
}

/** ACADEMIA / ALPHA. Una sola familia, dos pesos, y el mismo tracking en las dos líneas. */
function Palabra({ color, centrado, escala = 1 }) {
  const px = (n) => `${Math.max(8, Math.round(n * escala))}px`;
  return (
    <div className={`leading-none ${centrado ? "text-center" : ""}`}>
      <div
        style={{ color, opacity: 0.92, fontSize: px(11), letterSpacing: "0.42em", textIndent: "0.42em" }}
        className="font-light uppercase"
      >
        Academia
      </div>
      <div
        style={{ color, fontSize: px(15), letterSpacing: "0.42em", textIndent: "0.42em", marginTop: px(7) }}
        className="font-semibold uppercase"
      >
        Alpha
      </div>
    </div>
  );
}
